import React, { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { HeroVisualStack, RhythmSection, ShowcaseGallery } from "@/components/landing/VisualEngine";
import { Helmet } from "react-helmet-async";
import SiteFooter from "@/components/SiteFooter";
import LangSwitcher from "@/components/LangSwitcher";
import ExitIntentOffer from "@/components/ExitIntentOffer";
import { VideoUploader } from "@/components/VideoUploader";
import { useI18n } from "@/i18n/useI18n";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Zap, ArrowRight, Check, X, Sparkles, RefreshCw, Gauge, ShieldCheck,
  Twitter, Facebook, Instagram, Linkedin, Code2, Store, Rocket, Star, Bot,
  Wand2, Loader2, Copy, CalendarDays, PlayCircle, Lock, CreditCard, CheckCircle2, XCircle, Clock,
} from "lucide-react";

const demoEmbedSrc = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/);
  if (yt) return { type: "iframe", src: `https://www.youtube.com/embed/${yt[1]}?autoplay=1&rel=0` };
  const vim = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vim) return { type: "iframe", src: `https://player.vimeo.com/video/${vim[1]}?autoplay=1` };
  return { type: "video", src: url };
};
import { toast } from "sonner";

const handleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const ANGLES = ["Problem/Solution", "Behind The Scenes", "Concrete Spec", "Social Proof", "Quick Action"];

// Rotating hero preview — shows the range of ad styles the engine produces across niches.
const HERO_EXAMPLES = [
  {
    brand: "Northwind Coffee Co.", initial: "N", mark: "NORTHWIND", accent: "hsl(25 70% 42%)",
    img: "https://images.unsplash.com/photo-1620820186187-fc32e79adb74?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    platform: "linkedin", tag: "Social Proof", day: 12,
    headline: ["4,000 cups poured.", "Zero burnt beans."],
    subheadline: "Small-batch roasted the morning it ships — that's why regulars keep coming back.",
    caption: "We roast every batch the morning it ships — so your first cup tastes exactly like it should. This week only: free shipping on your first bag.",
    hashtags: "#specialtycoffee · #smallbatch · #freshroast",
  },
  {
    brand: "Lumen Skincare", initial: "L", mark: "LUMEN", accent: "hsl(200 80% 48%)",
    img: "https://images.unsplash.com/photo-1713768704571-6aeb0d0e5105?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    platform: "facebook", tag: "Concrete Spec", day: 8,
    headline: ["1 drop.", "8 hours of glow."],
    subheadline: "Clinically-tested hyaluronic serum that holds 1,000x its weight in water.",
    caption: "Your skin drinks it in — 92% of testers saw visibly smoother skin in 14 days. No fillers, no fragrance, just results.",
    hashtags: "#skincare #hyaluronic #glowup",
  },
  {
    brand: "Drift Apparel", initial: "D", mark: "DRIFT", accent: "hsl(222 55% 46%)",
    img: "https://images.unsplash.com/photo-1556905055-8f358a7a47b2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    platform: "linkedin", tag: "Quick Action", day: 21,
    headline: ["Built to last.", "Priced to move."],
    subheadline: "Organic cotton basics that survive 100+ washes without fading.",
    caption: "The tee you'll reach for every week — heavyweight organic cotton, pre-shrunk, ethically made. Restock is live, sizes going fast.",
    hashtags: "#sustainablefashion · #organiccotton · #wardrobe",
  },
];


const FAQ_ITEMS = [
  { q: "How does pricing work?", a: "Three plans: Creator ($29/mo) for solo creators & founders — unlimited 1-click 30-day campaigns and automated publishing; E-commerce Growth ($79/mo) adds Shopify catalog sync, inventory-aware auto-swapping and the AI Strategy Consultant; and Agency ($199/mo) for teams — up to 25 connected Facebook & LinkedIn profiles, 10 team & client seats, 50GB shared media storage, unlimited AI caption & hashtag generation, and a priority publishing queue with automated token-health monitoring. Save 2 months with annual billing. Cancel anytime." },
  { q: "Can I cancel anytime?", a: "Yes. Every plan is month-to-month (or annual if you want 2 months free) and you can cancel in one click from the billing page whenever you like — no emails, no phone calls." },
  { q: "What does \"anti-slop\" actually mean?", a: "Our engine bans generic AI filler — no \"unleash\", no rhetorical-question openers, no fake excitement. Every post uses concrete details from your product and stays within platform-native limits on emojis and hashtags." },
  { q: "Which platforms can I publish to?", a: "Facebook and LinkedIn. You connect accounts in a couple of clicks — no pasting raw account IDs." },
  { q: "Do I need my own API keys?", a: "No. Everything works out of the box. Advanced users can optionally bring their own keys, but it's not required." },
  { q: "How is this cheaper than Buffer, Ocoya or Predis?", a: "AutoGridSocial is built for ultra-low infrastructure cost, so we pass the savings on — plans start at $29/mo for unlimited campaigns instead of the usual $99+/mo enterprise markup, with Shopify automation built in." },
];


const PLATFORM_ICON = { twitter: Twitter, facebook: Facebook, linkedin: Linkedin, instagram: Instagram };
const hookColor = (s) =>
  s >= 8 ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/40"
  : s >= 5 ? "bg-amber-500/20 text-amber-400 border-amber-500/40"
  : "bg-rose-500/20 text-rose-400 border-rose-500/40";

const TONES = [
  { id: "authoritative expert", label: "Authoritative Expert" },
  { id: "tactical and direct", label: "Tactical & Direct" },
  { id: "witty and sharp", label: "Witty & Sharp" },
  { id: "warm and human", label: "Warm & Human" },
];
const SAMPLE_ICON = { twitter: Twitter, linkedin: Linkedin, instagram: Instagram, facebook: Facebook };

const CAL_PHASES = [
  { n: "Week 1 · Awareness & Education", dot: "bg-cyan-400", cell: "border-cyan-500/40 bg-cyan-500/10", text: "text-cyan-300" },
  { n: "Week 2 · Social Proof", dot: "bg-indigo-400", cell: "border-indigo-500/40 bg-indigo-500/10", text: "text-indigo-300" },
  { n: "Week 3 · Soft Pitch", dot: "bg-amber-400", cell: "border-amber-500/40 bg-amber-500/10", text: "text-amber-300" },
  { n: "Week 4 · Direct Offer", dot: "bg-emerald-400", cell: "border-emerald-500/40 bg-emerald-500/10", text: "text-emerald-300" },
];
const CAL_ICONS = [Linkedin, Facebook];
const phaseOf = (d) => (d <= 7 ? 0 : d <= 14 ? 1 : d <= 21 ? 2 : 3);

const OLD_NEW_ROWS = [
  ["Disconnected, random daily posts", "A strategic 30-day narrative: Awareness → Proof → Pitch → Offer"],
  ["40+ minutes a day writing & scheduling", "A full month generated and queued in under 60 seconds"],
  ["Generic AI slop and emoji soup", "Anti-slop, zero-emoji, concrete specs and real numbers"],
  ["A $5,000/mo agency — or no strategy at all", "A built-in AI Consultant auditing your strategy 24/7"],
  ["$99+/mo enterprise pricing", "Plans from $29/mo — with Shopify automation built in"],
  ["Manual copy-paste into every app", "Automated webhook dispatch + one-tap Quick Publish"],
];

// Example feed = real engine output: AI photo posts mixed with spec cards, quote cards and PDF carousels.
const SC = (n) => `/showcase/${n}.webp`;
const FERNWOOD = { brand_name_detected: "Fernwood Coffee", brand_accent: "#F97316" };
const DEMO_SCANS = [
  {
    brand: FERNWOOD,
    posts: [
      { platform: "facebook", platform_label: "Facebook", full_image: SC("photo_coldbrew"), badge: "Photo Post", post_text: "Cold brew tastes flat when the beans are old.\n\nOurs ship 72 hours after roasting, so the first glass is the best one.", hashtags: ["#coldbrew", "#freshroast"] },
      { platform: "linkedin", platform_label: "LinkedIn", full_image: SC("spec"), badge: "Spec Breakdown", post_text: "Most coffee sits in a warehouse for weeks.\n\nOurs goes from roaster to doorstep in 72 hours, in a 340g compostable bag.", hashtags: ["#supplychain"] },
      { platform: "linkedin", platform_label: "LinkedIn", full_image: SC("photo_roaster"), badge: "Founder Log", post_text: "We roast in 12kg batches, never more.\n\nSmaller drums mean we can taste every batch before it ships.", hashtags: ["#smallbatch", "#founderlog"] },
    ],
  },
  {
    brand: FERNWOOD,
    posts: [
      { platform: "facebook", platform_label: "Facebook", full_image: SC("photo_bag"), badge: "Photo Post", post_text: "Your pour-over deserves better than supermarket beans.\n\nSingle-origin Huila, 340g, free shipping over $35.", hashtags: ["#pourover", "#specialtycoffee"] },
      { platform: "linkedin", platform_label: "LinkedIn", full_image: SC("slide1"), badge: "PDF Carousel", post_text: "3 numbers behind a better morning cup.\n\nSwipe the 5-slide breakdown: roast date, brew ratio, grind size.", hashtags: ["#coffeeeducation"] },
      { platform: "facebook", platform_label: "Facebook", full_image: SC("hottake"), badge: "Hot Take", post_text: "Dark roast isn't stronger.\n\nCaffeine drops the longer beans roast. Try our medium Huila for a real kick.", hashtags: ["#coffeefacts"] },
    ],
  },
];
const BADGE_TONE = {
  "Photo Post": "text-rose-200 border-rose-400/40 bg-black/60", "Spec Breakdown": "text-sky-200 border-sky-400/40 bg-black/60",
  "Founder Log": "text-emerald-200 border-emerald-400/40 bg-black/60", "Hot Take": "text-amber-200 border-amber-400/40 bg-black/60",
  "PDF Carousel": "text-fuchsia-200 border-fuchsia-400/40 bg-black/60",
};

function LivePreviewWidget({ onStart }) {
  const [url, setUrl] = useState("");
  const [posts, setPosts] = useState([]);
  const [brand, setBrand] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [demoIdx, setDemoIdx] = useState(0);
  const showDemo = !busy && posts.length === 0;
  useEffect(() => {
    if (!showDemo) return;
    const t = setInterval(() => setDemoIdx((d) => (d + 1) % DEMO_SCANS.length), 5000);
    return () => clearInterval(t);
  }, [showDemo]);
  const displayPosts = posts.length ? posts : DEMO_SCANS[demoIdx].posts;
  const displayBrand = posts.length ? brand : DEMO_SCANS[demoIdx].brand;

  const run = async () => {
    const u = url.trim();
    if (u.length < 3) { setErr("Enter your website URL (e.g. yourbrand.com)."); return; }
    setBusy(true); setErr(""); setPosts([]); setBrand(null);
    try {
      const r = await api.post("/tools/url-scan", { url: u });
      setPosts(r.data.posts || []);
      setBrand(r.data.brand || null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not scan that site right now. Please try again.");
    }
    setBusy(false);
  };
  const copy = async (txt) => { try { await navigator.clipboard.writeText(txt); toast.success("Copied"); } catch (e) {} };

  return (
    <section id="live-preview" aria-label="Scan your website" className="border-t border-border/60 bg-card/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-10">
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-accent">Scan your website · no signup</span>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2">Enter your store URL. Watch it build your feed.</h2>
          <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">We read your brand from your site and instantly generate a real, anti-slop 3-post feed for Facebook and LinkedIn — the same engine behind your full 30-day campaign.</p>
        </div>
        <div className="max-w-5xl mx-auto rounded-2xl border border-border bg-card p-5 sm:p-7">
          <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
            <div className="flex-1 min-w-0">
              <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Your Business Website</label>
              <input data-testid="urlscan-input" value={url} onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && run()}
                placeholder="Paste your website link to extract your brand voice & offer..."
                className="mt-1.5 w-full h-12 rounded-lg border border-border bg-background px-4 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/50" />
            </div>
            <Button data-testid="urlscan-generate-btn" onClick={run} disabled={busy}
              className="h-12 px-6 bg-primary hover:bg-primary/90 text-white shrink-0">
              {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Analyzing…</> : <><Wand2 className="mr-2 h-4 w-4" /> Analyze Brand DNA →</>}
            </Button>
          </div>
          {brand?.brand_name_detected && (
            <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground" data-testid="urlscan-brand">
              {brand.brand_logo_url && <img src={brand.brand_logo_url} alt="" className="h-5 w-5 rounded object-contain bg-white/80" />}
              Detected brand: <span className="text-foreground font-medium">{brand.brand_name_detected}</span>
              {brand.brand_accent && <span className="inline-block h-3 w-3 rounded-full border border-border" style={{ backgroundColor: brand.brand_accent }} />}
            </div>
          )}
          {err && <p className="text-sm text-rose-400 mt-4" data-testid="urlscan-error">{err}</p>}

          <div className="mt-6" data-testid="urlscan-cards">
            {busy ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[0, 1, 2].map((i) => <div key={i} className="rounded-xl border border-border bg-background/60 h-56 animate-pulse" />)}
              </div>
            ) : (
              <>
                {showDemo && (
                  <div className="flex items-center justify-center gap-2 mb-4 text-xs text-muted-foreground" data-testid="urlscan-demo-label">
                    <span className="inline-block h-2 w-2 rounded-full animate-pulse" style={{ background: displayBrand?.brand_accent }} />
                    Example — <span className="text-foreground font-medium">{displayBrand?.brand_name_detected}</span>. Scan your own site above for yours.
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {displayPosts.map((p, i) => {
                    const Icon = SAMPLE_ICON[p.platform] || Facebook;
                    const bname = displayBrand?.brand_name_detected || "Your Brand";
                    const accent = displayBrand?.brand_accent || "#6366F1";
                    const initial = (bname[0] || "B").toUpperCase();
                    const headline = p.headline || (p.post_text || "").slice(0, 48);
                    return (
                      <div key={`${showDemo ? "d" + demoIdx : "r"}-${i}`} className="rounded-xl border border-border bg-card overflow-hidden flex flex-col animate-fadeUp" style={{ animationDelay: `${i * 0.08}s` }} data-testid={`urlscan-card-${i}`}>
                        {/* social post header */}
                        <div className="flex items-center justify-between px-4 pt-4 pb-3">
                          <div className="flex items-center gap-2.5 min-w-0">
                            {displayBrand?.brand_logo_url ? (
                              <img src={displayBrand.brand_logo_url} alt="" className="h-8 w-8 rounded-lg object-contain bg-white/90 shrink-0" />
                            ) : (
                              <div className="h-8 w-8 rounded-lg grid place-items-center shrink-0 text-white font-display font-extrabold text-sm" style={{ background: accent }}>{initial}</div>
                            )}
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-foreground leading-tight truncate">{bname}</div>
                              <div className="flex items-center gap-1 text-[11px] text-muted-foreground"><Icon className="h-3 w-3" /> Sponsored</div>
                            </div>
                          </div>
                          <button onClick={() => copy(p.post_text)} className="text-muted-foreground hover:text-primary shrink-0" data-testid={`urlscan-copy-${i}`}><Copy className="h-3.5 w-3.5" /></button>
                        </div>
                        {p.full_image ? (
                          <div className="relative aspect-square mx-4 rounded-lg overflow-hidden border border-white/10 shadow-lg shadow-black/30 bg-[#0D1117]">
                            <img src={p.full_image} alt={`${p.badge} example`} loading="lazy" className="absolute inset-0 w-full h-full object-cover object-top" />
                            <span className={`absolute top-2 left-2 text-[10px] font-semibold px-2 py-1 rounded-md border backdrop-blur ${BADGE_TONE[p.badge] || ""}`} data-testid={`urlscan-badge-${i}`}>{p.badge}</span>
                          </div>
                        ) : (
                        /* generated branded graphic — uses the store's REAL product photo when available */
                        <div className="relative aspect-[1200/630] mx-4 rounded-lg overflow-hidden border border-white/10 shadow-lg shadow-black/30 bg-[#0b0b16]"
                          style={p.image_url ? undefined : { background: `radial-gradient(130% 130% at 80% -10%, ${accent} 0%, #16172e 48%, #0b0b16 100%)` }}>
                          {p.image_url && (
                            <img src={p.image_url} alt="" className="absolute inset-0 w-full h-full object-cover"
                              onError={(e) => { e.currentTarget.style.display = "none"; }} />
                          )}
                          <div className="absolute inset-0 pointer-events-none"
                            style={{ background: p.image_url
                              ? `linear-gradient(120deg, ${accent}e6 0%, ${accent}66 36%, rgba(11,11,22,0.55) 72%, rgba(11,11,22,0.30) 100%)`
                              : undefined }} />
                          <div className="absolute inset-0 opacity-[0.10] pointer-events-none" style={{ backgroundImage: "linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)", backgroundSize: "22px 22px" }} />
                          <div className="relative h-full flex flex-col justify-between p-4">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-[8px] font-semibold tracking-[0.16em] uppercase px-2 py-1 rounded-full border border-white/30 bg-white/10 text-white">{p.platform_label}</span>
                              <span className="font-display font-extrabold text-white/90 text-[11px] truncate max-w-[45%]">{bname}</span>
                            </div>
                            <div>
                              <div className="font-display text-base sm:text-lg font-extrabold text-white leading-[1.06] tracking-tight drop-shadow-[0_2px_12px_rgba(0,0,0,0.4)]">{headline}</div>
                              {p.subheadline && <div className="text-white/80 text-[11px] mt-1.5 leading-snug">{p.subheadline}</div>}
                            </div>
                            <div className="h-1 w-10 rounded-full bg-white/90" />
                          </div>
                        </div>
                        )}
                        {/* caption + hashtags */}
                        <div className="px-4 pt-3 pb-4 flex flex-col flex-1">
                          <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap flex-1">{p.post_text}</p>
                          {p.hashtags?.length > 0 && <p className="text-xs font-mono text-accent mt-3">{p.hashtags.join(" ")}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6 rounded-xl border border-primary/30 bg-primary/5 p-4">
                  <p className="text-sm text-foreground/90">
                    {showDemo
                      ? <span>A real mix: AI photo posts, spec cards, quote cards and PDF carousels. <span className="font-semibold text-primary">Scan your own store</span> to see yours.</span>
                      : <span>That's 3 of <span className="font-semibold text-primary">30</span>. Auto-schedule a full month to Facebook &amp; LinkedIn in one click.</span>}
                  </p>
                  <Button data-testid="urlscan-upsell-cta" onClick={() => { try { if (url.trim().length >= 3) localStorage.setItem("ags_scan_url", url.trim()); } catch (e) {} onStart(); }} className="bg-primary hover:bg-primary/90 text-white shrink-0 group">Build my 30-day campaign <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function AnimatedCalendar({ onStart }) {
  const [filled, setFilled] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setFilled((f) => (f >= 30 ? 0 : f + 1)), 130);
    return () => clearInterval(iv);
  }, []);
  return (
    <section aria-label="30-day campaign preview" className="border-t border-border/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-10">
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-accent">1 click · 30 days</span>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2">A whole month, structured like a campaign</h2>
          <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">Not 30 random posts. A strategic narrative that warms your audience up and moves them to action — generated and queued automatically.</p>
        </div>
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-6">
          {CAL_PHASES.map((p) => (<span key={p.n} className="flex items-center gap-2 text-xs text-muted-foreground"><span className={`h-2 w-2 rounded-full ${p.dot}`} /> {p.n}</span>))}
        </div>
        <div className="max-w-4xl mx-auto rounded-2xl border border-border bg-card p-4 sm:p-6" data-testid="animated-calendar-container">
          <div className="grid grid-cols-6 sm:grid-cols-10 gap-2">
            {Array.from({ length: 30 }, (_, i) => {
              const d = i + 1; const ph = CAL_PHASES[phaseOf(d)]; const on = i < filled; const Icon = CAL_ICONS[i % CAL_ICONS.length];
              return (
                <div key={d} data-testid={`calendar-day-card-${d}`}
                  className={`aspect-square rounded-lg border flex flex-col items-center justify-center transition-all duration-300 ${on ? ph.cell : "border-border bg-background/40"}`}>
                  <span className={`text-[10px] font-mono ${on ? ph.text : "text-muted-foreground/40"}`}>{d}</span>
                  {on && <Icon className={`h-3.5 w-3.5 mt-0.5 ${ph.text}`} />}
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between mt-5 pt-4 border-t border-border">
            <span className="text-sm text-muted-foreground"><span className="font-semibold text-foreground">{filled}</span>/30 posts queued</span>
            <Button data-testid="calendar-start-cta" onClick={onStart} size="sm" className="bg-primary hover:bg-primary/90 text-white group">Generate mine <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-1" /></Button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const { user, loading, logout } = useAuth();
  const { t, lang } = useI18n();
  const faqItems = t("landing.faq", FAQ_ITEMS);
  const navigate = useNavigate();
  const siteOrigin = typeof window !== "undefined" ? window.location.origin : "https://autogridsocial.com";
  const [angle, setAngle] = useState(0);
  const [heroEx, setHeroEx] = useState(0);
  const [promoAll, setPromoAll] = useState([]);
  const [win, setWin] = useState(0);
  const [showConsent, setShowConsent] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const pendingLogin = useRef(null);
  const [demoVideoUrl, setDemoVideoUrl] = useState("");
  const [demoOpen, setDemoOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [priceInterval, setPriceInterval] = useState("monthly");
  useEffect(() => { api.get("/site/settings").then((r) => setDemoVideoUrl(r.data?.demo_video_url || "")).catch(() => {}); }, []);
  useEffect(() => { api.get("/auth/me").then((r) => setIsAdmin(!!r.data?.is_admin)).catch(() => setIsAdmin(false)); }, []);


  const startLogin = (before) => {
    if (localStorage.getItem("gs_legal_consent") === "1") {
      if (before) before();
      localStorage.setItem("gs_legal_consent", "1");
      handleLogin();
      return;
    }
    pendingLogin.current = before || null;
    setShowConsent(true);
  };

  const confirmConsent = () => {
    if (!agreed) return;
    localStorage.setItem("gs_legal_consent", "1");
    setShowConsent(false);
    if (pendingLogin.current) pendingLogin.current();
    handleLogin();
  };

  useEffect(() => {
    // Capture a referral code from ?ref= so it attaches on signup
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get("ref");
      if (ref) localStorage.setItem("gs_ref_code", ref);
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    const t = setInterval(() => setAngle((a) => (a + 1) % ANGLES.length), 1800);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setHeroEx((e) => (e + 1) % HERO_EXAMPLES.length), 4500);
    return () => clearInterval(t);
  }, []);

  // Fetch promo posts, refetch every 60s to pick up freshly generated ones
  useEffect(() => {
    const load = () => api.get("/promo/examples?limit=12").then((r) => setPromoAll(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  // Auto-refresh: rotate the visible window of cards every 7s
  useEffect(() => {
    if (promoAll.length <= 6) return;
    const t = setInterval(() => setWin((w) => (w + 3) % promoAll.length), 7000);
    return () => clearInterval(t);
  }, [promoAll.length]);

  const promo = promoAll.length
    ? Array.from({ length: Math.min(6, promoAll.length) }, (_, k) => promoAll[(win + k) % promoAll.length])
    : [];

  const startFromPromo = (p) => {
    startLogin(() => {
      try {
        if (p?.angle_used) {
          localStorage.setItem("aps_ref_angle", p.angle_used);
          if (p.platform) localStorage.setItem("aps_ref_platform", p.platform);
          api.post("/promo/track", { type: "click", angle: p.angle_used, platform: p.platform }).catch(() => {});
        }
      } catch (e) {}
    });
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <title>Generate & Schedule 30 Days of Social Content in 1 Click | AutoGridSocial</title>
        <meta name="description" content="AutoGridSocial writes and schedules a full 30-day, on-brand Facebook & LinkedIn campaign in one click — with Shopify catalog sync, inventory-aware auto-swapping and an AI Strategy Consultant. No AI slop. Plans from $29/mo." />
        <link rel="canonical" href={siteOrigin + (lang === "es" ? "/es" : "/")} />
        <link rel="alternate" hrefLang="en" href={siteOrigin + "/"} />
        <link rel="alternate" hrefLang="es" href={siteOrigin + "/es"} />
        <link rel="alternate" hrefLang="x-default" href={siteOrigin + "/"} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Generate & Schedule 30 Days of Social Content in 1 Click | AutoGridSocial" />
        <meta property="og:description" content="One click turns your brand into a strategic 30-day Facebook & LinkedIn campaign — with Shopify sync and an AI Strategy Consultant built in. Plans from $29/mo." />
        <meta property="og:url" content={siteOrigin + "/"} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "AutoGridSocial",
          url: siteOrigin,
          description: "AutoGridSocial helps developers, creators, and small businesses turn their content into consistent social media posts and distribute them across every platform.",
          brand: "AutoGridSocial",
          legalName: "Beckett LLC",
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "AutoGridSocial",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          description: "Anti-slop AI that writes and schedules concrete, on-brand social posts for Facebook and LinkedIn.",
          offers: [
            { "@type": "Offer", price: "29", priceCurrency: "USD", name: "Creator" },
            { "@type": "Offer", price: "79", priceCurrency: "USD", name: "E-commerce Growth" },
            { "@type": "Offer", price: "199", priceCurrency: "USD", name: "Agency" },
          ],
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqItems.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "How to automate your social media posts with AutoGridSocial",
          description: "Generate concrete, on-brand posts and schedule them across Facebook and LinkedIn.",
          step: [
            { "@type": "HowToStep", position: 1, name: "Set up your brand", text: "Create a workspace with your niche, tone and key product facts." },
            { "@type": "HowToStep", position: 2, name: "Generate anti-slop posts", text: "The AI writes concrete, on-brand posts with no buzzword filler." },
            { "@type": "HowToStep", position: 3, name: "Schedule automatically", text: "Drop posts on the calendar at your best times and publish on autopilot." },
          ],
        })}</script>
      </Helmet>
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <a href="#main" className="flex items-center gap-2" aria-label="AutoGridSocial home">
            <img src="/brand-icon.png" alt="AutoGridSocial" className="h-8 w-8 rounded-lg" />
            <span className="font-display font-bold text-lg tracking-tight">AutoGridSocial</span>
          </a>
          <nav aria-label="Primary" className="hidden md:flex items-center gap-6 text-sm text-muted-foreground">
            <a href="#features" className="hover:text-foreground transition-colors" data-testid="nav-jump-features">{t("nav.features", "Features")}</a>
            <a href="#comparison" className="hover:text-foreground transition-colors" data-testid="nav-jump-comparison">{t("nav.comparison", "Comparison")}</a>
            <a href="#pricing" className="hover:text-foreground transition-colors" data-testid="nav-jump-pricing">{t("nav.pricing", "Pricing")}</a>
            <a href="#faq" className="hover:text-foreground transition-colors" data-testid="nav-jump-faq">{t("nav.faq", "FAQ")}</a>
            <Link to="/#live-preview" className="hover:text-foreground transition-colors" data-testid="nav-jump-analyze">{t("nav.analyze", "Analyze Brand DNA")}</Link>
            <Link to="/blog" className="hover:text-foreground transition-colors" data-testid="nav-jump-guides">{t("nav.guides", "Guides")}</Link>
          </nav>
          <div className="flex items-center gap-2">
            <LangSwitcher />
            {user ? (
              <>
                <Button data-testid="nav-dashboard-button" onClick={() => navigate(user.is_admin ? "/admin" : "/dashboard")} variant="ghost" className="text-sm">
                  {user.is_admin ? t("nav.admin", "Admin") : t("nav.dashboard", "Dashboard")}
                </Button>
                <Button data-testid="nav-logout-button" onClick={logout} variant="outline" className="text-sm border-border">
                  {t("nav.signout", "Sign out")}
                </Button>
              </>
            ) : (
              <>
                <Button data-testid="nav-login-button" onClick={() => startLogin()} variant="ghost" className="text-sm">{t("nav.signin", "Sign in")}</Button>
                <Button data-testid="nav-start-trial-button" onClick={() => startLogin()} className="bg-primary hover:bg-primary/90 text-white text-sm hidden sm:inline-flex">
                  {t("nav.trial", "Get started")}
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <main id="main">
      {/* Hero */}
      <section className="relative overflow-hidden" aria-label="Product overview">
        <div className="absolute inset-0 grid-noise pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[420px] w-[820px] rounded-full bg-primary/10 blur-[120px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div className="animate-fadeUp">
              <Badge className="mb-6 bg-secondary text-foreground border-border font-mono text-xs">
                <Sparkles className="h-3.5 w-3.5 mr-1.5 text-accent" /> {t("landing.hero_badge3", "Brand DNA from your URL · AI photos · spec cards · PDF carousels")}
              </Badge>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mb-6">
                {t("landing.hero_title1", "Generate & schedule")}
                <span className="block mt-2 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
                  {t("landing.hero_title2b", "30 days of posts built from your real specs")}
                </span>
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground max-w-xl mb-8 leading-relaxed">
                {t("landing.hero_sub2", "Paste your URL. We pull your product specs and real numbers, write a month of Facebook & LinkedIn posts that cite them, and pair them with AI product photos, spec cards, quote cards and swipeable PDF carousels.")}
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <Button data-testid="landing-google-login-button" onClick={() => startLogin()} size="lg"
                  className="bg-primary hover:bg-primary/90 text-white h-12 px-7 text-base group">
                  {t("nav.trial", "Get started")}
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
                <Link to="/start" data-testid="landing-try-demo-button" className="inline-flex">
                  <Button size="lg" variant="outline"
                    className="h-12 px-7 text-base font-semibold border-2 border-accent/60 text-accent hover:bg-accent/10 hover:border-accent group">
                    <Sparkles className="mr-2 h-4 w-4" /> {t("landing.try_demo", "Try the free demo")}
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
                {demoVideoUrl && (
                  <Button data-testid="landing-watch-demo-button" onClick={() => setDemoOpen(true)} size="lg" variant="ghost"
                    className="h-12 px-5 text-base text-foreground/90 hover:text-foreground group">
                    <PlayCircle className="mr-2 h-5 w-5 text-accent" /> Watch demo
                  </Button>
                )}
                <div className="flex items-center gap-2 text-muted-foreground text-sm max-w-xs">
                  <Gauge className="h-4 w-4 text-accent shrink-0" /> {t("landing.hero_micro", "Built for indie devs and creators — concrete, on-brand posts without the $99/mo markup.")}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-5 text-sm" data-testid="hero-trust">
                <span className="flex items-center gap-1.5 text-muted-foreground"><ShieldCheck className="h-4 w-4 text-emerald-400" /> {t("landing.trust_trial", "$1 for 7 days · full 30-day campaign included")}</span>
                <span className="flex items-center gap-1.5 text-muted-foreground"><Lock className="h-4 w-4 text-emerald-400" /> {t("landing.trust_stripe", "Secure Stripe checkout · no card to explore")}</span>
              </div>
              <div className="flex items-center gap-6 mt-10">
                <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{t("landing.publishes_to", "Publishes to")}</span>
                <div className="flex items-center gap-4 text-muted-foreground">
                  {[Facebook, Linkedin].map((Icon, i) => (
                    <Icon key={i} className="h-5 w-5 hover:text-foreground transition-colors" />
                  ))}
                </div>
              </div>
            </div>

            {/* Preview card — real engine output rotating through the 4-day archetype rhythm */}
            <div className="animate-fadeUp" style={{ animationDelay: "0.12s" }}>
              <HeroVisualStack />
            </div>
          </div>
        </div>
      </section>

      <RhythmSection onStart={() => startLogin()} />
      <ShowcaseGallery />
      <LivePreviewWidget onStart={() => startLogin()} />
      <AnimatedCalendar onStart={() => startLogin()} />

      {/* Impact metrics banner */}
      <section className="border-t border-border/60 bg-card/40" data-testid="impact-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4">
            {[
              { stat: "80%", label: t("landing.impact.s1", "Less time spent writing posts"), sub: t("landing.impact.s1sub", "Specs in, on-brand posts out") },
              { stat: "30 Days", label: t("landing.impact.s2", "Evergreen queue on autopilot"), sub: t("landing.impact.s2sub", "Set it once, it keeps posting") },
              { stat: "$1,200+", label: t("landing.impact.s3", "Saved a year vs $99/mo tools"), sub: t("landing.impact.s3sub", "Same output, indie pricing") },
              { stat: "100%", label: t("landing.impact.s4", "Anti-slop, on-brand output"), sub: t("landing.impact.s4sub", "Hard guardrails on every post") },
            ].map((m, i) => (
              <div key={m.stat} className="text-center lg:text-left px-2 animate-fadeUp"
                style={{ animationDelay: `${i * 0.08}s` }} data-testid={`impact-stat-${i}`}>
                <div className="font-display text-4xl lg:text-5xl font-extrabold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  {m.stat}
                </div>
                <div className="text-sm font-medium text-foreground mt-2">{m.label}</div>
                <div className="text-xs text-muted-foreground mt-1">{m.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Anti-slop comparison */}
      <section id="antislop" aria-label="Comparison with generic AI tools" className="border-t border-border/60 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-primary/15 text-primary border-primary/30">{t("landing.cmp_badge", "Anti-Slop AI Output")}</Badge>
            <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight">{t("landing.cmp_title", "Not another buzzword generator")}</h2>
            <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">
              {t("landing.cmp_sub", "Hard guardrails force concrete specs and stats into every post — and ban the filler most AI tools spit out.")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                tag: "Product update",
                bad: "🚀 Thrilled to announce we're revolutionizing the space with next-level features! Big things are coming to unlock a whole new tapestry of value. ✨🔥",
                good: "New scheduling engine cuts your posting time from 40 minutes a day to 5. Set it once, stay consistent all month.",
              },
              {
                tag: "SaaS launch",
                bad: "We're beyond excited to elevate your workflow with a game-changing, revolutionary dashboard that will unlock your team's true potential! 🙌📈🔥",
                good: "New dashboard loads in 40ms and cuts report setup from 9 clicks to 2. Same data, half the busywork.",
              },
              {
                tag: "App update",
                bad: "In today's fast-paced world, look no further — our mastering-level update is here to elevate everything you love. Big things coming! 🚀✨🎉",
                good: "v3.1 shrinks the app to 18MB and adds offline mode. Opens in under a second, even on 3G.",
              },
            ].map((ex) => (
              <React.Fragment key={ex.tag}>
                <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2 text-rose-400 font-semibold">
                      <X className="h-5 w-5" /> Generic AI tools
                    </div>
                    <Badge variant="outline" className="text-[10px] border-border text-muted-foreground">{ex.tag}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground italic leading-relaxed">"{ex.bad}"</p>
                </div>
                <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2 text-emerald-400 font-semibold">
                      <Check className="h-5 w-5" /> AutoGridSocial
                    </div>
                    <Badge variant="outline" className="text-[10px] border-emerald-500/30 text-emerald-400">{ex.tag}</Badge>
                  </div>
                  <p className="text-sm text-foreground/90 leading-relaxed">"{ex.good}"</p>
                </div>
              </React.Fragment>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mt-6">
            <div className="rounded-xl border border-rose-500/20 bg-rose-500/[0.03] p-5">
              <ul className="space-y-2 text-xs text-rose-300/80">
                {["Buzzwords & hype", "Emoji soup", "Zero concrete detail"].map((t) => (
                  <li key={t} className="flex items-center gap-2"><X className="h-3.5 w-3.5" /> {t}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.03] p-5">
              <ul className="space-y-2 text-xs text-emerald-300/90">
                {["Real specs & stats", "Max 2 emojis, 3 hashtags", "Platform-native length"].map((t) => (
                  <li key={t} className="flex items-center gap-2"><Check className="h-3.5 w-3.5" /> {t}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Value ladder — 30-day engine + AI Consultant */}
      <section id="value-ladder" aria-label="Value ladder" className="border-t border-border/60 bg-card/30" data-testid="value-ladder-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-primary/15 text-primary border-primary/30">The upgrade most tools can't offer</Badge>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">More than a post generator</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-primary/40 bg-card p-7 relative overflow-hidden">
              <div className="absolute top-0 right-0 h-40 w-40 bg-primary/15 blur-3xl" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-4"><div className="h-11 w-11 rounded-xl bg-primary flex items-center justify-center"><CalendarDays className="h-6 w-6 text-white" /></div><Badge className="bg-primary/15 text-primary border-primary/30">Anchor feature</Badge></div>
                <h3 className="font-display text-xl font-bold">1-Click 30-Day Campaign Engine</h3>
                <p className="text-sm text-muted-foreground mt-2 mb-5">Standard generators give you disconnected, random posts. Ours builds a strategic month:</p>
                <div className="space-y-2">
                  {[["Week 1", "Awareness & Education"], ["Week 2", "Social Proof & Case Studies"], ["Week 3", "Soft Pitch"], ["Week 4", "Direct Offer"]].map(([w, l], i) => (
                    <div key={w} className="flex items-center gap-3 text-sm"><span className={`h-2 w-2 rounded-full ${CAL_PHASES[i].dot}`} /><span className="font-mono text-xs text-muted-foreground w-16">{w}</span><span className="text-foreground/90">{l}</span></div>
                  ))}
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-accent/40 bg-card p-7 relative overflow-hidden" data-testid="ai-consultant-card">
              <div className="absolute top-0 right-0 h-40 w-40 bg-accent/15 blur-3xl" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-4"><div className="h-11 w-11 rounded-xl bg-accent/20 flex items-center justify-center"><Bot className="h-6 w-6 text-accent" /></div><Badge className="bg-accent/15 text-accent border-accent/30">Built in</Badge></div>
                <h3 className="font-display text-xl font-bold">Your AI Strategy Consultant</h3>
                <p className="text-sm text-foreground/90 mt-2 mb-4">Like having a <span className="text-accent font-semibold">$5,000/mo social media director</span> auditing your strategy, spotting content gaps, and refining your voice — 24/7.</p>
                <ul className="space-y-2.5">
                  {["Audits your profile and flags what's missing", "Writes strategy cards with the exact post + best time", "Learns your brand voice and content pillars"].map((x) => (
                    <li key={x} className="flex items-start gap-2.5 text-sm text-muted-foreground"><Check className="h-4 w-4 text-accent mt-0.5 shrink-0" /> {x}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Old way vs New way */}
      <section id="comparison" aria-label="Old way vs new way" className="border-t border-border/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-12">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">The old way vs the AutoGridSocial way</h2>
            <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">Why teams switch from generic schedulers, AI-slop tools, and $5,000/mo agencies.</p>
          </div>
          <div className="rounded-2xl border border-border overflow-hidden" data-testid="comparison-table">
            <div className="grid grid-cols-2">
              <div className="p-4 sm:p-5 bg-rose-500/[0.06] border-b border-r border-border flex items-center gap-2 text-rose-300 font-semibold" data-testid="old-way-col"><X className="h-4 w-4" /> The old way</div>
              <div className="p-4 sm:p-5 bg-emerald-500/[0.06] border-b border-border flex items-center gap-2 text-emerald-300 font-semibold" data-testid="new-way-col"><Check className="h-4 w-4" /> AutoGridSocial</div>
              {OLD_NEW_ROWS.map(([bad, good], i) => (
                <React.Fragment key={i}>
                  <div className={`p-4 sm:p-5 text-sm text-muted-foreground border-r border-border ${i < OLD_NEW_ROWS.length - 1 ? "border-b" : ""}`} data-testid={`old-row-${i}`}>{bad}</div>
                  <div className={`p-4 sm:p-5 text-sm text-foreground/90 ${i < OLD_NEW_ROWS.length - 1 ? "border-b border-border" : ""}`} data-testid={`new-row-${i}`}>{good}</div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Smart Angle Rotation */}
      <section id="features" aria-label="Features" className="border-t border-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <Badge className="mb-4 bg-accent/15 text-accent border-accent/30">{t("landing.feat_badge", "Smart Angle Rotation")}</Badge>
              <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight mb-4">
                {t("landing.feat_title", "Same product, five fresh angles")}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                {t("landing.feat_sub", "Buckets don't repeat themselves. Each generation rotates the marketing angle — so your evergreen queue never sounds like a broken record, and search engines never flag spam.")}
              </p>
              <div className="flex flex-wrap gap-2">
                {ANGLES.map((a, i) => (
                  <span key={a}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                      i === angle ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground"
                    }`}>
                    {a}
                  </span>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: Code2, label: t("landing.feat_c1", "Developers"), desc: t("landing.feat_c1d", "Product updates & launches") },
                { icon: Store, label: t("landing.feat_c2", "Small businesses"), desc: t("landing.feat_c2d", "Offers & announcements") },
                { icon: Rocket, label: t("landing.feat_c3", "Creators"), desc: t("landing.feat_c3d", "Drops & behind-the-scenes") },
              ].map((c) => (
                <div key={c.label} className="rounded-xl border border-border bg-card p-5 card-hover">
                  <c.icon className="h-6 w-6 text-primary mb-3" />
                  <div className="font-semibold text-sm">{c.label}</div>
                  <div className="text-xs text-muted-foreground mt-1">{c.desc}</div>
                </div>
              ))}
              <div className="sm:col-span-3 rounded-xl border border-border bg-card p-5 flex items-center gap-3">
                <ShieldCheck className="h-6 w-6 text-emerald-400 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  {t("landing.feat_note", "Hard-expires after 3 recycles + ±7min posting jitter to keep you off bot-detection radars.")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Live self-promo — the product marketing itself */}
      {promo.length > 0 && (
        <section className="border-t border-border/60 bg-card/30" data-testid="self-promo-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center mb-8">
              <Badge className="mb-4 bg-accent/15 text-accent border-accent/30">
                <Bot className="h-3.5 w-3.5 mr-1.5" /> {t("landing.promo_badge", "Running on its own autopilot")}
              </Badge>
              <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight">
                {t("landing.promo_title", "Generated by the engine itself")}
              </h2>
              <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">
                {t("landing.promo_sub", "We point AutoGridSocial at AutoGridSocial. These are live, unedited posts our own anti-slop engine generated to market this product — proof it practices what it preaches.")}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto animate-fadeUp" key={win}>
              {promo.slice(0, 2).map((p) => {
                const Icon = PLATFORM_ICON[p.platform] || Twitter;
                return (
                  <div key={p.id} className="rounded-xl border border-border bg-card p-5 card-hover flex flex-col"
                    data-testid={`promo-card-${p.id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-muted-foreground">
                        <Icon className="h-4 w-4" /> {p.platform}
                      </div>
                      {typeof p.hook_score === "number" && (
                        <Badge className={`border ${hookColor(p.hook_score)}`}>Hook {p.hook_score}/10</Badge>
                      )}
                    </div>
                    {p.image_path ? (
                      <div className="relative rounded-lg overflow-hidden border border-border h-40 mb-3">
                        <img src={`${API}/media/${p.image_path}`} alt="promo graphic" className="w-full h-full object-cover" />
                        {p.visual_overlay_text && (
                          <div className="absolute inset-0 flex items-center justify-center px-3 bg-gradient-to-t from-black/60 to-transparent">
                            <span className="font-display text-xl font-extrabold text-white uppercase tracking-tight text-center drop-shadow-[0_2px_10px_rgba(0,0,0,0.85)]">
                              {p.visual_overlay_text}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : p.visual_overlay_text ? (
                      <div className="rounded-lg border border-border bg-gradient-to-br from-primary/20 via-secondary to-accent/10 h-28 flex items-center justify-center mb-3 px-3">
                        <span className="font-display text-xl font-extrabold text-white uppercase tracking-tight text-center drop-shadow-[0_2px_10px_rgba(0,0,0,0.7)]">
                          {p.visual_overlay_text}
                        </span>
                      </div>
                    ) : null}
                    <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap line-clamp-6 flex-1">{p.post_text}</p>
                    <div className="flex items-center gap-2 mt-4 pt-3 border-t border-border text-xs text-muted-foreground">
                      <RefreshCw className="h-3.5 w-3.5 text-primary" />
                      <span className="font-mono text-primary">{p.angle_used}</span>
                      <span className="ml-auto">{p.category}</span>
                    </div>
                    <Button data-testid={`promo-cta-${p.id}`}
                      variant="ghost" size="sm" className="mt-2 w-full text-primary hover:bg-primary/10 group" onClick={() => startFromPromo(p)}>
                      Get started
                      <ArrowRight className="ml-1.5 h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </div>
                );
              })}
            </div>

            {/* Agency-focused upsell — Shopify auto-sync + white-label client portals */}
            <div className="mt-10 max-w-4xl mx-auto rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/10 via-card to-accent/5 p-6 sm:p-8"
              data-testid="agency-upsell-banner">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <div className="flex items-start gap-3">
                  <div className="h-11 w-11 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
                    <Store className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm">{t("landing.upsell_ecom_title", "Shopify auto-sync")}</div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t("landing.upsell_ecom_desc", "Live catalog sync auto-swaps out-of-stock products so campaigns never promote sold-out inventory.")}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="h-11 w-11 rounded-xl bg-accent/15 flex items-center justify-center shrink-0">
                    <ShieldCheck className="h-5 w-5 text-accent" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm">{t("landing.upsell_wl_title", "White-label client portals")}</div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t("landing.upsell_wl_desc", "Agencies invite clients via passwordless magic links to review, comment on, and approve entire campaigns.")}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 pt-5 border-t border-border/60">
                <p className="text-sm text-foreground/90 text-center sm:text-left">
                  {t("landing.upsell_cta_line", "Built for agencies & multi-brand teams — manage up to 25 Facebook & LinkedIn profiles on the Agency plan.")}
                </p>
                <Button data-testid="agency-upsell-cta"
                  onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}
                  className="w-full sm:w-auto shrink-0 bg-primary hover:bg-primary/90 text-white group">
                  {t("landing.upsell_cta_btn", "See Agency plan")}
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ===== 2.0 Launch module: founding banner + before/after + autonomous + trust ===== */}
      <section className="border-t border-border/60" data-testid="launch-module">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-14">
          {/* Founding member banner */}
          <a href="#pricing" data-testid="founding-banner"
            className="block rounded-2xl border border-primary/50 bg-gradient-to-r from-primary/15 via-card to-accent/10 px-6 py-5 hover:border-primary transition-colors">
            <div className="flex items-center gap-3 flex-wrap justify-center text-center">
              <Badge className="bg-primary/20 text-primary border-primary/40"><Rocket className="h-3.5 w-3.5 mr-1.5" /> AutoGridSocial 2.0 Launch</Badge>
              <p className="text-sm sm:text-base font-semibold">
                Lock in <span className="text-primary">Founding Member pricing</span> — 3-Month ($79.99) &amp; 6-Month ($179.99) plans
              </p>
              <ArrowRight className="h-4 w-4 text-primary" />
            </div>
          </a>

          {/* Before vs After */}
          <div>
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight uppercase">Generic AI slop vs. AutoGridSocial</h2>
              <p className="text-muted-foreground mt-3">Same prompt. The difference your feed actually sees.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              <div className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-6" data-testid="before-card">
                <div className="flex items-center gap-2 mb-3 text-rose-400 text-xs font-mono uppercase tracking-wider"><XCircle className="h-4 w-4" /> Typical AI slop</div>
                <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-line">
                  🚀 In today's fast-paced world, it's crucial to unlock your potential! 

Our game-changing solution will revolutionize your workflow and skyrocket your results. ✨

Don't miss out — let's dive in and elevate your business to the next level! 💯 #innovation #growth #success</p>
              </div>
              <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-6" data-testid="after-card">
                <div className="flex items-center gap-2 mb-3 text-emerald-400 text-xs font-mono uppercase tracking-wider"><CheckCircle2 className="h-4 w-4" /> AutoGridSocial anti-slop</div>
                <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-line">
                  Most teams lose 6+ hours a week formatting posts by hand.

That time doesn't show up on a P&L. It just quietly disappears.

We write the week, match each post to its peak window, and publish it for you.

This isn't for everyone — if you enjoy the manual grind, skip it.

Comment "SYSTEM" and I'll send how it works.</p>
              </div>
            </div>
          </div>

          {/* Autonomous scheduling highlight */}
          <div className="rounded-2xl border border-border bg-card/60 p-8 max-w-5xl mx-auto grid md:grid-cols-[auto_1fr] gap-6 items-center" data-testid="autonomous-highlight">
            <div className="h-14 w-14 rounded-2xl bg-primary/15 flex items-center justify-center shrink-0 mx-auto">
              <Clock className="h-7 w-7 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">Zero-Click Peak-Timing Scheduler for Facebook &amp; LinkedIn</h3>
              <p className="text-sm text-muted-foreground mt-2">
                Posts are automatically scheduled and published during each platform's high-traffic engagement windows —
                LinkedIn's weekday professional peaks, Facebook's evening &amp; weekend leisure windows — spaced to avoid
                algorithmic suppression. Fully hands-free. No clicks, no approvals.
              </p>
            </div>
          </div>

          {/* Trust stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto" data-testid="trust-stats">
            {[
              { icon: Sparkles, stat: "100%", label: "Unique Copy Output Guarantee" },
              { icon: ShieldCheck, stat: "0%", label: "AI Clichés Enforced" },
              { icon: Lock, stat: "256-bit", label: "SSL · Powered by Stripe" },
              { icon: Check, stat: "1-Click", label: "Cancel Any Time · No Contracts" },
            ].map((s, i) => (
              <div key={i} className="rounded-xl border border-border bg-card p-5 text-center">
                <s.icon className="h-5 w-5 text-primary mx-auto mb-2" />
                <div className="text-2xl font-display font-extrabold">{s.stat}</div>
                <div className="text-xs text-muted-foreground mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" aria-label="Pricing plans" className="border-t border-border/60 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight uppercase">{t("landing.price_title", "Simple, transparent pricing")}</h2>
            <p className="text-muted-foreground mt-3">{t("landing.price_sub", "Autonomous visual merchandising for creators, Shopify stores, and agencies.")}</p>
          </div>

          {/* Billing interval toggle — mirrors the in-app Billing page */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex rounded-full border border-border bg-card p-1 flex-wrap justify-center" data-testid="landing-interval-toggle">
              {[
                { id: "monthly", label: "Monthly" },
                { id: "quarterly", label: "3-Month" },
                { id: "biannual", label: "6-Month" },
                { id: "yearly", label: "Yearly", tag: "2 mo free" },
              ].map((iv) => (
                <button key={iv.id} data-testid={`landing-interval-${iv.id}`} onClick={() => setPriceInterval(iv.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${priceInterval === iv.id ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>
                  {iv.label}{iv.tag && <span className="ml-1 text-emerald-400">· {iv.tag}</span>}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto items-stretch">
            {[
              { key: "creator", name: "Creator", prices: { monthly: 29, quarterly: 79.99, biannual: 179.99, yearly: 290 },
                sub: "For solo creators & founders", cta: "Choose Creator", icon: Zap, highlight: false, badge: null,
                feats: [
                  { text: "Unlimited 1-Click 30-Day Campaigns", included: true },
                  { text: "Facebook & LinkedIn publishing", included: true },
                  { text: "Up to 5 connected profiles", included: true },
                  { text: "1 team seat · 2GB media storage", included: true },
                  { text: "Automated webhook publishing", included: true },
                  { text: "Basic Keyword Ideas (Google Autocomplete Suggestions)", included: true },
                  { text: "Shopify sync & AI Consultant", included: false },
                ] },
              { key: "growth", name: "E-commerce Growth", prices: { monthly: 79, quarterly: 213, biannual: 399, yearly: 790 },
                sub: "For Shopify & online stores", cta: "Choose Growth", icon: Rocket, highlight: true, badge: "★ Most Popular",
                feats: [
                  { text: "Everything in Creator", included: true },
                  { text: "Shopify catalog sync + auto-swap", included: true },
                  { text: "AI Strategy Consultant (50/mo)", included: true },
                  { text: "Up to 10 connected profiles", included: true },
                  { text: "3 team & client seats · 10GB storage", included: true },
                  { text: "100 Monthly Exact Search Volume Lookups (DataForSEO)", included: true },
                  { text: "Keywords to Facebook & LinkedIn draft builder", included: true },
                  { text: "Priority generation speed", included: true },
                ] },
              { key: "agency", name: "Agency", prices: { monthly: 199, quarterly: 537, biannual: 999, yearly: 1990 },
                sub: "For agencies & multi-brand teams", cta: "Choose Agency", icon: Star, highlight: false, badge: null,
                feats: [
                  { text: "Everything in E-commerce Growth", included: true },
                  { text: "Up to 25 Facebook & LinkedIn profiles", included: true },
                  { text: "10 team & client seats", included: true },
                  { text: "50GB shared media asset storage", included: true },
                  { text: "500 Monthly Exact Search Volume Lookups (DataForSEO)", included: true },
                  { text: "Keyword CSV export & team sharing", included: true },
                  { text: "Unlimited AI caption & hashtag generation", included: true },
                  { text: "Priority queue + token health monitoring", included: true },
                ] },
            ].map((p) => {
              const cadenceMap = { monthly: "/mo", quarterly: "/3mo", biannual: "/6mo", yearly: "/yr" };
              const price = p.prices[priceInterval];
              const cadence = cadenceMap[priceInterval];
              const perMonth = priceInterval === "quarterly" ? price / 3 : priceInterval === "biannual" ? price / 6 : null;
              return (
              <div key={p.key} className={`rounded-2xl border ${p.highlight ? "border-primary/50" : "border-border"} bg-card overflow-hidden flex flex-col`}>
                <div className="p-6 border-b border-border relative">
                  {p.highlight && <div className="absolute top-0 right-0 h-32 w-32 bg-primary/15 blur-3xl" />}
                  <div className="relative flex items-center justify-between">
                    <div>
                      <Badge className={`mb-3 ${p.highlight ? "bg-primary/15 text-primary border-primary/30" : "bg-secondary text-foreground border-border"}`}>
                        {p.badge ? <><Star className="h-3.5 w-3.5 mr-1.5" /> {p.badge}</> : p.name}
                      </Badge>
                      <div className="text-sm font-semibold text-foreground mb-1">{p.name}</div>
                      <div className="flex items-end gap-2">
                        <span className="text-4xl font-display font-extrabold" data-testid={`landing-price-${p.key}`}>${price}</span>
                        <span className="text-muted-foreground mb-1.5">{cadence}</span>
                      </div>
                      {perMonth && <p className="text-xs text-emerald-400 mt-1" data-testid={`landing-permonth-${p.key}`}>${perMonth.toFixed(2)}/mo · billed {priceInterval === "quarterly" ? "every 3 months" : "every 6 months"}</p>}
                      {priceInterval === "yearly" && <p className="text-xs text-emerald-400 mt-1">2 months free</p>}
                      <p className="text-sm text-muted-foreground mt-2">{p.sub}</p>
                      <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 px-3 py-1 text-xs font-semibold text-emerald-400"
                        data-testid={`pricing-${p.key}-trial-badge`}>
                        <Zap className="h-3 w-3" /> {t("landing.trial_badge", "$1 for 7 days")}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        {t("landing.trial_line", "Then")} ${price}{cadence} {t("landing.trial_line2", "· cancel anytime")}
                      </p>
                    </div>
                    <div className={`h-12 w-12 rounded-2xl flex items-center justify-center ${p.highlight ? "bg-primary" : "bg-secondary"}`}>
                      <p.icon className={`h-6 w-6 ${p.highlight ? "text-white" : "text-foreground"}`} />
                    </div>
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <ul className="space-y-3 mb-8 flex-1">
                    {p.feats.map((f) => (
                      <li key={f.text} className={`flex items-start gap-3 text-sm ${f.included ? "" : "text-muted-foreground/60"}`}>
                        <span className={`h-5 w-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${f.included ? "bg-emerald-500/20" : "bg-rose-500/15"}`}>
                          {f.included ? <Check className="h-3 w-3 text-emerald-400" /> : <X className="h-3 w-3 text-rose-400" />}
                        </span>{f.text}
                      </li>
                    ))}
                  </ul>
                  <Button data-testid={`pricing-${p.key}-button`} onClick={() => startLogin()}
                    className={`w-full h-12 text-base group ${p.highlight ? "bg-primary hover:bg-primary/90 text-white" : "border border-primary/40 text-primary bg-transparent hover:bg-primary/10"}`}>
                    {p.cta}
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </div>
              </div>
              );
            })}
          </div>
          <p className="text-center text-sm text-muted-foreground mt-8 flex items-center justify-center gap-2" data-testid="pricing-trial-note">
            <ShieldCheck className="h-4 w-4 text-emerald-400" /> {t("landing.price_note", "Every plan starts with a 7-day trial for $1 — including a full 30-day campaign. Billed at your plan price on day 7. Cancel anytime.")}
          </p>
        </div>
      </section>

      {/* Why trust us — value demonstration + risk reduction (no fake reviews) */}
      <section aria-label="Why trust us" className="border-t border-border/60 bg-card/30" data-testid="why-trust-us">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="text-center mb-12">
            <span className="text-xs font-mono uppercase tracking-[0.2em] text-accent">Behind the build</span>
            <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight mt-2">{t("landing.trust_title", "Built to a standard, not a hype cycle")}</h2>
            <p className="text-muted-foreground mt-3 max-w-xl mx-auto">{t("landing.trust_sub", "We're brand new, so we won't fake reviews. Here's how the product earns your trust from day one.")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: ShieldCheck, title: "Anti-slop by design", desc: "Hard guardrails on every post — no filler words, capped emojis and hashtags, concrete specs and real numbers pulled from your own site." },
              { icon: Lock, title: "Secure by default", desc: "Payments run through Stripe — your card details never touch our servers. Every request is served over encrypted HTTPS." },
              { icon: RefreshCw, title: "Zero lock-in", desc: "Month-to-month, cancel in one click from the billing page. No contracts, no cancellation calls, no dark patterns." },
              { icon: Bot, title: "Your data, your accounts", desc: "We publish to your own connected Facebook & LinkedIn. We never sell followers, engagement, or your data." },
            ].map((p, i) => (
              <div key={i} className="rounded-2xl border border-border bg-card p-6 animate-fadeUp" style={{ animationDelay: `${i * 0.08}s` }} data-testid={`trust-pillar-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-primary/15 text-primary flex items-center justify-center mb-4"><p.icon className="h-5 w-5" /></div>
                <h3 className="font-display font-bold">{p.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>

          {/* Security / trust badges */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3" data-testid="trust-badges">
            {[
              { icon: Lock, label: "Secure checkout by Stripe" },
              { icon: ShieldCheck, label: "256-bit SSL encryption" },
              { icon: CreditCard, label: "No card to start exploring" },
              { icon: CheckCircle2, label: "Cancel anytime" },
            ].map((b, i) => (
              <span key={i} className="inline-flex items-center gap-2 rounded-full border border-border bg-background/60 px-4 py-2 text-xs font-medium text-muted-foreground" data-testid={`trust-badge-${i}`}>
                <b.icon className="h-4 w-4 text-emerald-400" /> {b.label}
              </span>
            ))}
          </div>

          {/* Founder note */}
          <div className="mt-10 rounded-2xl border border-border bg-card p-6 sm:p-8 flex flex-col sm:flex-row gap-6 items-start" data-testid="founder-note">
            <img src="/founder-sebastian.jpg" alt="Sebastian Beckett, founder of AutoGridSocial"
              className="h-20 w-20 sm:h-24 sm:w-24 rounded-2xl object-cover shrink-0 border border-border" />
            <div>
              <span className="text-xs font-mono uppercase tracking-[0.2em] text-accent">Behind the build</span>
              <blockquote className="mt-2 text-base sm:text-lg text-foreground/90 leading-relaxed">
                “I built AutoGridSocial after watching agencies and multi-brand teams bleed billable hours on the same grind: posting inconsistently, chasing client approvals across endless email threads, and paying brutal per-seat markups to bloated legacy tools. I come from a Catholic background and believe in strong family values — so this is built to be honest and to give teams their time back: on-brand posts that sound like each client, a one-tap approval link instead of email ping-pong, and fair pricing with no enterprise seat tax. No hype, no fake numbers.”
              </blockquote>
              <div className="mt-4 flex items-center gap-2">
                <span className="font-display font-bold">Sebastian Beckett</span>
                <span className="text-sm text-muted-foreground">· Founder, AutoGridSocial</span>
              </div>
            </div>
          </div>

          {/* Risk-reversal band near CTA */}
          <div className="mt-12 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-5 text-center sm:text-left" data-testid="guarantee-badge">
            <div className="w-14 h-14 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center shrink-0"><ShieldCheck className="h-7 w-7" /></div>
            <div className="flex-1">
              <h3 className="font-display text-xl font-bold">{t("landing.guarantee_title", "Try it risk-free — cancel anytime")}</h3>
              <p className="text-sm text-muted-foreground mt-1">{t("landing.guarantee_sub", "Plans start at a simple $29/mo. Not blown away? Cancel in one click from the billing page — no emails, no forms, no questions.")}</p>
            </div>
            <Button onClick={() => startLogin()} size="lg" className="bg-primary hover:bg-primary/90 text-white h-12 px-7 shrink-0 group" data-testid="guarantee-cta">
              {t("nav.trial", "Get started")}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        </div>
      </section>

      <section id="faq" aria-label="Frequently asked questions" className="border-t border-border/60">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="mb-12">
            <span className="text-xs font-mono uppercase tracking-wider text-accent">{t("landing.faq_kicker", "FAQ")}</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">{t("landing.faq_title", "Questions, answered")}</h2>
            <p className="text-muted-foreground mt-3 max-w-lg">{t("landing.faq_sub", "Everything you need to know before you get started.")}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8" data-testid="faq-list">
            {faqItems.map((f, i) => (
              <div key={i} className="border-l-2 border-primary/30 pl-5" data-testid={`faq-item-${i}`}>
                <h3 className="font-semibold text-base mb-2">{f.q}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      </main>
      <SiteFooter />
      <ExitIntentOffer />

      {isAdmin && (
        <div className="fixed bottom-4 left-4 z-50 rounded-xl border border-primary/40 bg-card/95 backdrop-blur p-3 shadow-xl" data-testid="landing-admin-video">
          <p className="text-[11px] font-semibold text-primary mb-2">Admin · demo video</p>
          <VideoUploader compact onDone={(url) => setDemoVideoUrl(url)} />
          {demoVideoUrl && <p className="text-[10px] text-muted-foreground mt-1 max-w-[220px] truncate">Live: {demoVideoUrl}</p>}
        </div>
      )}

      <Dialog open={demoOpen} onOpenChange={setDemoOpen}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden bg-black border-border" data-testid="demo-video-modal">
          <DialogHeader className="sr-only"><DialogTitle>Product demo</DialogTitle></DialogHeader>
          {(() => {
            const e = demoEmbedSrc(demoVideoUrl);
            if (!e) return null;
            return e.type === "iframe" ? (
              <div className="aspect-video w-full">
                <iframe src={e.src} title="AutoGridSocial demo" className="w-full h-full" frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
              </div>
            ) : (
              <video src={e.src} controls autoPlay className="w-full aspect-video bg-black" />
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={showConsent} onOpenChange={(o) => { setShowConsent(o); if (!o) setAgreed(false); }}>
        <DialogContent className="sm:max-w-md" data-testid="consent-dialog">
          <DialogHeader>
            <DialogTitle>Before you start</DialogTitle>
            <DialogDescription>Confirm you agree to our terms to create your account.</DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-3 py-2 cursor-pointer">
            <Checkbox data-testid="consent-checkbox" checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5" />
            <span className="text-sm text-muted-foreground">
              I agree to the{" "}
              <a href="/terms" target="_blank" rel="noreferrer" className="text-primary underline">Terms of Service</a>{" "}
              and{" "}
              <a href="/privacy" target="_blank" rel="noreferrer" className="text-primary underline">Privacy Policy</a>.
            </span>
          </label>
          <Button data-testid="consent-continue" onClick={confirmConsent} disabled={!agreed}
            className="w-full bg-primary hover:bg-primary/90 text-white">
            Continue to sign in
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
